import React, { useState, useEffect, useContext, useRef } from 'react';


import OrderTable from './OrderTable.jsx';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { forceWholesaleTerritory, shouldCreateOpportunities, shouldSeeDealerFields } from '../private/permissions.js';
import ZohoCustomerSearch from './ZohoCustomerSearch.jsx';
import { Context } from './StockManagerContext.jsx';
import { Card, Col, Row } from 'react-bootstrap';
import { post } from './utils.js';

const states = __KEAP_ENV__ === 'SWAU' ? {
  'AU-ACT': 'Australian Capital Territory',
  'AU-NSW': 'New South Wales',
  'AU-NT': 'Northern Territory',
  'AU-QLD': 'Queensland',
  'AU-SA': 'South Australia',
  'AU-TAS': 'Tasmania',
  'AU-VIC': 'Victoria',
  'AU-WA': 'Western Australia'
} : {
  'NZ-NTL': 'Northland',
  'NZ-AUK': 'Auckland',
  'NZ-WKO': 'Waikato',
  'NZ-BOP': 'Bay of Plenty',
  'NZ-GIS': 'Gisborne',
  'NZ-HKB': 'Hawke\'s Bay',
  'NZ-TKI': 'Taranaki',
  'NZ-MWT': 'Manawatū-Whanganui',
  'NZ-WGN': 'Wellington',
  'NZ-TAS': 'Tasman',
  'NZ-NSN': 'Nelson',
  'NZ-MBH': 'Marlborough',
  'NZ-WTC': 'West Coast',
  'NZ-CAN': 'Canterbury',
  'NZ-OTA': 'Otago',
  'NZ-STL': 'Southland',
}

const getOptions = (field) => {

  if (field === 'states') {
    return Object.entries(states).map(([key, state]) => ({
      id: key,
      label: state
    }));
  }
};

const formatDate = function (date) {
    if (!date) return '';
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// Shared editor for an address (shipping or billing). All fields are required except
// company and street2; blank required fields are marked invalid while editing.
function AddressEditor({ title, prefix, emptyLabel, address, editing, onEdit, onClose, onCopyFromCustomer, onCopyFromOther, copyFromOtherLabel, updateAddress }) {
  const blank = key => !address?.[key];

  // Brief green + tick confirmation after a copy action.
  const [justCopied, setJustCopied] = useState(null); // 'customer' | 'other'
  const flash = (which, fn) => {
    fn();
    setJustCopied(which);
    setTimeout(() => setJustCopied(prev => (prev === which ? null : prev)), 1200);
  };
  const copiedStyle = which => justCopied === which
    ? { color: '#28a745', transition: 'color .3s' }
    : { transition: 'color .3s' };

  if (!editing) {
    return (
      <>
        <Form.Label>{title}: <a href="#" onClick={e => { e.preventDefault(); onEdit(); }}><small>Edit</small></a></Form.Label>
        <div style={{ color: '#666', marginBottom: 12 }}>
          {address && Object.keys(address).length > 0 ?
            <small>
              <div>
                {[address.firstName, address.lastName].filter(Boolean).join(' ')}
                {address.company && ` (${address.company})`}
              </div>
              <div>
                {address.street1}
                {address.street2 && `, ${address.street2}`}
              </div>
              <div>
                {[address.city, address.province].filter(Boolean).join(', ')}
                {address.postcode && ` ${address.postcode}`}
              </div>
              <div>
                {address.country && (address.country === 'AU' ? 'Australia' : address.country === 'NZ' ? 'New Zealand' : address.country)}
              </div>
              <div>
                {address.phone}
              </div>
            </small>
            : <em>{emptyLabel}</em>
          }
        </div>
      </>
    );
  }

  return (
    <Card bg="light" className="mb-3">
      <Card.Body>
      <Form.Label>{title}: <a href="#" onClick={e => { e.preventDefault(); onClose(); }}><small>Close</small></a></Form.Label>

      <div style={{ marginBottom: 4 }}>
        <small>
          <a href="#" onClick={e => { e.preventDefault(); flash('customer', onCopyFromCustomer); }} style={copiedStyle('customer')}>
            Copy from Zoho{justCopied === 'customer' && ' ✓'}
          </a>
          {onCopyFromOther && copyFromOtherLabel &&
            <>&nbsp;&middot;&nbsp;<a href="#" onClick={e => { e.preventDefault(); flash('other', onCopyFromOther); }} style={copiedStyle('other')}>
              {copyFromOtherLabel}{justCopied === 'other' && ' ✓'}
            </a></>
          }
        </small>
      </div>

      <div style={{ marginBottom: 8 }}>
        <small className="text-muted"><em>Changes here will be saved to the Zoho customer when you save this shipment.</em></small>
      </div>

      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} First Name:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('firstName')} type="text" value={address?.firstName || ''} onChange={e => updateAddress({ firstName: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Last Name:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('lastName')} type="text" value={address?.lastName || ''} onChange={e => updateAddress({ lastName: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Company:</Form.Label>
        <Col sm={8}>
          <Form.Control type="text" value={address?.company || ''} onChange={e => updateAddress({ company: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Phone:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('phone')} type="text" value={address?.phone || ''} onChange={e => updateAddress({ phone: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Street:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('street1')} type="text" value={address?.street1 || ''} onChange={e => updateAddress({ street1: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Unit/Apartment Number:</Form.Label>
        <Col sm={8}>
          <Form.Control type="text" value={address?.street2 || ''} onChange={e => updateAddress({ street2: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} City:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('city')} type="text" value={address?.city || ''} onChange={e => updateAddress({ city: e.target.value })} />
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Province:</Form.Label>
        <Col sm={8}>
          <Form.Control
            required
            isInvalid={blank('province')}
            as="select"
            value={address?.country && address?.province ? `${address.country}-${address.province}` : ''}
            onChange={e => {
              const country = e.target.value.split('-')[0];
              const province = e.target.value.split('-')[1];
              updateAddress({ province, country });
            }}
          >
            <option value=""></option>
            {getOptions('states').map(option =>
              <option value={option.id} key={option.id}>{option.label}</option>
            )}
          </Form.Control>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-2">
        <Form.Label column sm={4}>{prefix} Postal Code:</Form.Label>
        <Col sm={8}>
          <Form.Control required isInvalid={blank('postcode')} type="text" value={address?.postcode || ''} onChange={e => updateAddress({ postcode: e.target.value })} />
        </Col>
      </Form.Group>
      </Card.Body>
    </Card>
  );
}


export default function OrderForm({ getMethods }) {

  const [show, setShow] = useState(false);
  const [editingShippingAddress, setEditingShippingAddress] = useState(false);
  const [editingBillingAddress, setEditingBillingAddress] = useState(false);
  const [billingEdited, setBillingEdited] = useState(false);
  const [shippingEdited, setShippingEdited] = useState(false);

  // Invalid reasons for child components
  const [orderInvalidReasons, setOrderInvalidReasons] = useState([]);

  const getAddressFromCustomer = _ => {
    if (!currentCustomer) return {};
    if (currentCustomer.Type !== 'contact') return {};

    const firstName = currentCustomer.First_Name || currentCustomer.FirstName || (currentCustomer.Full_Name ? currentCustomer.Full_Name.split(' ')[0] : '') || '';
    const lastName = currentCustomer.Last_Name || currentCustomer.LastName || (currentCustomer.Full_Name ? currentCustomer.Full_Name.split(' ').slice(1).join(' ') : '') || '';
    const phone = currentCustomer.Mobile || currentCustomer.Phone || currentCustomer.Extension || '';
    const street1 = currentCustomer.Delivery_Street || currentCustomer.Billing_Address_Street || currentCustomer.Billing_Street || currentCustomer.Single_Line_15 || '';
    const street2 = currentCustomer.Delivery_Street_2 || currentCustomer.Billing_Street_2 || currentCustomer.Single_Line_14 || '';
    const city = currentCustomer.Delivery_City || currentCustomer.Billing_Address_City || currentCustomer.Billing_City || '';
    const provinceCode = currentCustomer.Delivery_State || currentCustomer.Billing_State_All || currentCustomer.Billing_State || '';
    const countryPrefix = __KEAP_ENV__ === 'SWAU' ? 'AU' : 'NZ';
    const postcode = currentCustomer.Delivery_Postcode || currentCustomer.Billing_Postcode || '';

    return {
      firstName,
      lastName,
      company: currentCustomer.Company || currentCustomer.Account_Name || '',
      phone,
      street1,
      street2,
      city,
      province: provinceCode,
      country: countryPrefix,
      postcode
    };
  }

  const getBillingAddressFromCustomer = _ => {
    if (!currentCustomer) return {};
    if (currentCustomer.Type !== 'contact') return {};

    const firstName = currentCustomer.First_Name || currentCustomer.FirstName || (currentCustomer.Full_Name ? currentCustomer.Full_Name.split(' ')[0] : '') || '';
    const lastName = currentCustomer.Last_Name || currentCustomer.LastName || (currentCustomer.Full_Name ? currentCustomer.Full_Name.split(' ').slice(1).join(' ') : '') || '';
    const phone = currentCustomer.Phone || currentCustomer.Mobile || '';
    const countryPrefix = __KEAP_ENV__ === 'SWAU' ? 'AU' : 'NZ';

    return {
      firstName,
      lastName,
      company: currentCustomer.Company || currentCustomer.Account_Name || '',
      phone,
      street1: currentCustomer.Billing_Address_Street || currentCustomer.Billing_Street || '',
      street2: currentCustomer.Billing_Street_2 || '',
      city: currentCustomer.Billing_Address_City || currentCustomer.Billing_City || '',
      province: currentCustomer.Billing_State_All || currentCustomer.Billing_State || '',
      country: countryPrefix,
      postcode: currentCustomer.Billing_Postcode || ''
    };
  }

  const handleShow = () => {
    setShow(true);
  };

  const handleClose = () => { reset(); }

  const defaultOrderData = {
    type: "order",
    dealerPO: "",
    dateRequired: "",
    salesType: "",
    shippingAddress: {},
    billingAddress: {},
    deposit: 0,
    isSkinny: false,
    note: "",
  };

  // Context
  const { currentCustomer,
          setCurrentCustomer,
          openCustomerForm,
          currentUser,
          getOrders,
          addNewOrder,
          updateOrder,
          salesTypes,
          warehouses
      } = useContext(Context);

  // Current order
  const [orderData, setOrderData] = useState(defaultOrderData);
  const [orderIdx, setOrderIdx] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedUpsells, setSelectedUpsells] = useState([]);
  const [saving, setSaving] = useState(false);

  // Determines if we should see Dealer PO and Dealer Name fields
  const showDealerFields = _ => shouldSeeDealerFields(currentUser.userRole);
  const wholesaleTerritoryOnly = _ => forceWholesaleTerritory(currentUser.userRole);
  const createOpportunities = _ => shouldCreateOpportunities(currentUser.userRole);

  // Is serialised?
  const isSerialised = products?.[0] && !products[0].enableQty;

  const customerAddress = getAddressFromCustomer();

  // Public methods
  useEffect(() => {
    getMethods({
      openToOrder: (idx, new_product = false) => {

        // Opens the form without saving.
        const order = getOrders()[idx];
        console.log('Opening order form to edit existing order', idx, order);

        setOrderIdx(idx);
        setOrderData(order.orderData);
        setProducts([...(order.products || []), new_product].filter(Boolean));
        setSelectedUpsells(order.selectedUpsells || []);

        handleShow();
      },
      openNewOrder: (product) => {
        setProducts([product]);

        // Copy address from previous order (if any)
        if ( getOrders().length > 0 ) {
          console.log('Copying shipping address from previous order');
          const orders = getOrders();
          const previousOrder = orders[orders.length - 1];

          setOrderData({
            ...orderData,
            shippingAddress: previousOrder.orderData.shippingAddress
          });
        }
        handleShow();
      },

    });

  }, []);

  // If only one sales type, set it as default
  useEffect(() => {

    if (salesTypes.length === 1) {
      setOrderData({
        ...orderData,
        salesType: salesTypes[0]['code']
      });

      defaultOrderData.salesType = salesTypes[0].code;
    }

  }, [salesTypes]);

  const addToOrderCb = async _ => {
    setSaving(true);

    // Maybe copy shipping address from customer
    // TODO - Remove this, and change the display logic in the order table instead
    console.log('Checking if we need to copy shipping address from customer', orderData.shippingAddress, getAddressFromCustomer());
    const _orderData = {...orderData};
    if ( !_orderData.shippingAddress || Object.keys(_orderData.shippingAddress).length === 0 ) {
      _orderData.shippingAddress = getAddressFromCustomer();
      setOrderData(_orderData); // Technically not needed...
    }
    if ( !_orderData.billingAddress || Object.keys(_orderData.billingAddress).length === 0 ) {
      _orderData.billingAddress = getBillingAddressFromCustomer();
    }

    const holdStockPromises = products.map(async (product) => {
      // Hold stock
      if (product.enableQty) {
          // Don't hold stock for virtual items
          return true;
      }

      console.log('Holding stock for product before adding to order', product);
      const response = await post('/api/row-action', { sku: product.sku, action: 'hold_stock' });
      if (response.status === 'success') {
          return true;

      } else {
          alert('Error holding stock: ' + response.error);
          return false;

      }

    });

    // Wait for all stock holds to complete
    const holdStockResults = await Promise.all(holdStockPromises);
    const allHeld = holdStockResults.every(r => r === true);

    setSaving(false);
    if (!allHeld) {
      return;
    }

    const productSubtotal = products.reduce((sum, product) => {
        const price = parseFloat(product.price) || 0;
        return sum + (price * (product.qty||1));
    }, 0);

    const selectedUpsellSubtotal = Object.values(selectedUpsells).reduce((sum, upsells) => {
        return sum + Object.entries(upsells).reduce((upsellSum, [sku, {qty, discount, price}]) => {
            if (qty <= 0) {
                return upsellSum;
            }

            return upsellSum + (price * qty) - discount;
        }, 0);
    }, 0);

    const discountTotal = products.reduce((sum, product) => sum + (product.discount || 0), 0);

    const calculatedTotal = productSubtotal + selectedUpsellSubtotal - discountTotal;
    _orderData.calculatedTotal = calculatedTotal;

    if (!_orderData.opportunityName) {
      _orderData.opportunityName = `${customerAddress?.firstName}_${customerAddress?.lastName}-${formatDate(new Date())}`;
    }

    // If either address was edited in the modal, persist it back to the Zoho contact and
    // Neto customer so the finalised order (which reads addresses from Neto) reflects it.
    if ((billingEdited || shippingEdited) && currentCustomer?.Type === 'contact' && currentCustomer?.Email) {
      setSaving(true);
      const countryName = __COUNTRY__ === 'NZ' ? 'New Zealand' : 'Australia';
      const contact = { Email: currentCustomer.Email };
      const neto = { EmailAddress: currentCustomer.Email };

      if (billingEdited) {
        const b = _orderData.billingAddress || {};
        Object.assign(contact, {
          First_Name: b.firstName,
          Last_Name: b.lastName,
          Phone: b.phone,
          Company: b.company,
          Billing_Address_Street: b.street1,
          Billing_Street_2: b.street2,
          Billing_Address_City: b.city,
          Billing_State_All: b.province,
          Billing_Postcode: b.postcode,
          Billing_Country: countryName,
        });
        neto.BillingAddress = {
          BillFirstName: b.firstName,
          BillLastName: b.lastName,
          BillCompany: b.company,
          BillPhone: b.phone,
          BillStreetLine1: b.street1,
          BillStreetLine2: b.street2,
          BillCity: b.city,
          BillState: b.province,
          BillPostCode: b.postcode,
          BillCountry: __COUNTRY__,
        };
      }

      if (shippingEdited) {
        const s = _orderData.shippingAddress || {};
        // Zoho contacts store delivery street on Single_Line_15/14. There are no
        // delivery name fields, so the installation contact name is kept on the order/Neto only.
        Object.assign(contact, {
          Mobile: s.phone,
          Single_Line_15: s.street1,
          Single_Line_14: s.street2,
          Delivery_City: s.city,
          Delivery_State: s.province,
          Delivery_Postcode: s.postcode,
          Delivery_Country: countryName,
        });
        neto.ShippingAddress = {
          ShipFirstName: s.firstName,
          ShipLastName: s.lastName,
          ShipCompany: s.company,
          ShipPhone: s.phone,
          ShipStreetLine1: s.street1,
          ShipStreetLine2: s.street2,
          ShipCity: s.city,
          ShipState: s.province,
          ShipPostCode: s.postcode,
          ShipCountry: __COUNTRY__,
        };
      }

      try {
        const resp = await post('/api/zoho/upsert-contact', JSON.stringify({ contact, neto }));
        if (!resp || !resp.success) {
          throw new Error(resp?.error ? JSON.stringify(resp.error) : 'Unknown error');
        }
      } catch (e) {
        console.error('Failed to update customer address', e);
        alert('Could not update the customer address in Zoho. Please try again.\n\n' + (e.message || ''));
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    // If editing an existing order, update it instead
    if (orderIdx !== null) {
        console.log('Updating existing order', orderIdx);
        const updatedOrder = {orderData: _orderData, products, selectedUpsells};
        updateOrder(orderIdx, updatedOrder);
    } else {

      console.log('Adding new order', {orderData: _orderData, products, selectedUpsells});
      addNewOrder({orderData: _orderData, products, selectedUpsells});
    }

    // Reset form
    reset();
  }

  const reset = _ => {
    setEditingShippingAddress(false);
    setEditingBillingAddress(false);
    setBillingEdited(false);
    setShippingEdited(false);
    setOrderIdx(null);
    setOrderData(defaultOrderData);
    setProducts([]);
    setSelectedUpsells([]);
    setShow(false);
  }

  const currentDate = new Date();
  const dateIsValid = () => {
    if (!orderData.dateRequired) {
      return false;
    }

    const selectedDate = new Date(orderData.dateRequired);

    if (selectedDate < currentDate) {
      return false;
    }

    return true;
  }

  // A Zoho contact must be selected before any addresses are shown or required.
  const hasContact = !!currentCustomer && currentCustomer.Type === 'contact';

  const shippingAddress = orderData.shippingAddress && Object.keys(orderData.shippingAddress).length > 0 ? orderData.shippingAddress : getAddressFromCustomer() || {};
  const billingAddress = orderData.billingAddress && Object.keys(orderData.billingAddress).length > 0 ? orderData.billingAddress : getBillingAddressFromCustomer() || {};

  // All address fields are required except company and street2 (unit/apartment).
  const requiredAddressKeys = ['firstName', 'lastName', 'phone', 'street1', 'city', 'province', 'postcode'];
  const addressComplete = addr => !!addr && requiredAddressKeys.every(key => !!addr[key]);
  const shippingComplete = addressComplete(shippingAddress);
  const billingComplete = addressComplete(billingAddress);

  // Merge a partial update into the shipping address (and flag shipping as edited).
  const updateShipping = partial => {
    setShippingEdited(true);
    setOrderData(prev => {
      const base = prev.shippingAddress && Object.keys(prev.shippingAddress).length > 0 ? prev.shippingAddress : getAddressFromCustomer();
      return { ...prev, shippingAddress: { ...base, ...partial } };
    });
  };

  // Merge a partial update into the billing address (and flag billing as edited).
  const updateBilling = partial => {
    setBillingEdited(true);
    setOrderData(prev => {
      const base = prev.billingAddress && Object.keys(prev.billingAddress).length > 0 ? prev.billingAddress : getBillingAddressFromCustomer();
      return { ...prev, billingAddress: { ...base, ...partial } };
    });
  };

  // When the modal opens, auto-expand any address that is missing required fields
  // so the rep is shown (and can fix) the incomplete fields. Runs at most once per
  // (open, customer) so re-renders can't re-force a section the user has closed.
  const autoExpandKey = useRef(null);
  useEffect(() => {
    if (!show) { autoExpandKey.current = null; return; }
    const key = (currentCustomer && currentCustomer.id) || 'none';
    if (autoExpandKey.current === key) return;
    autoExpandKey.current = key;
    setEditingShippingAddress(!addressComplete(shippingAddress));
    setEditingBillingAddress(!addressComplete(billingAddress));
  }, [show, currentCustomer]);

  const warehouseValid = () => {
    if (isSerialised) {
      return true;
    }
    if (orderData.warehouseCode) {
      return true;
    }
    return false;
  }

  const inValidReasons = [];

  if (!currentCustomer) {
    inValidReasons.push("Please select a customer first.");
  } else if (currentCustomer.Type !== 'contact') {
    inValidReasons.push("Please select a contact customer.");
  }

  if (salesTypes.length > 0 && !orderData.salesType) {
    inValidReasons.push("Please select a territory first.");
  }
  if (hasContact && !shippingComplete) {
    inValidReasons.push("Please complete the shipping address.");
  }
  if (hasContact && !billingComplete) {
    inValidReasons.push("Please complete the billing address.");
  }
  if (!dateIsValid()) {
    inValidReasons.push("Please select a valid delivery date.");
  }
  if (orderInvalidReasons.includes('upsells')) {
    inValidReasons.push("Please ensure all required upsells are selected.");
  }

  if (orderInvalidReasons.includes('deposit')) {
    inValidReasons.push("Please ensure the deposit is sufficient.");
  }

  if (!warehouseValid()) {
    inValidReasons.push("Please ensure a warehouse is selected.");
  }

  const isValid = inValidReasons.length === 0;

  const renderSalesTypes = salesTypes
    .filter(salesType => {

      let customerSalesType = false;
      if (currentCustomer && currentCustomer['Territory']) {
        customerSalesType = currentCustomer['Territory'];
      } else if (currentUser.territory) {
        customerSalesType = currentUser.territory;
      }

      // If no customer selected. Show all.
      if (!customerSalesType) {
        return true;
      }

      // Apply a user-level filter to sales types
      if (wholesaleTerritoryOnly()) {
        return salesType['code'].endsWith('W');
      }

      // If customer is dealer, only show that option.
      if (customerSalesType.substr(-1) === 'W') {
        return salesType['code'] === customerSalesType;
      }

      // If customer is retail, only show retail options
      if (customerSalesType.substr(-1) === 'R') {
        return salesType['code'].substr(-1) === 'R';
      }

      // Show all options otherwise
      return true;
    })
    .sort((a, b) => a['code'].localeCompare(b['code']));

  if (renderSalesTypes.length === 1 && orderData.salesType !== renderSalesTypes[0]['code']) {
    setOrderData({ ...orderData, salesType: renderSalesTypes[0]['code'] });
  }

  const now = new Date();
  const offset = now.getTimezoneOffset()
  const today = new Date(now.getTime() - (offset*60*1000))
  const date = today.toISOString().split('T')[0];

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{orderIdx !== null ? `Edit Shipment` : `New Shipment`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group as={Row}>
              <Col sm={6}>
                <Form.Label>Customer: { currentCustomer && currentCustomer.Type == 'contact' && <div>
                        {currentCustomer.Full_Name} {currentCustomer.Company && `(${currentCustomer.Company})`}
                        <a href="#" onClick={() => openCustomerForm()}><small>Edit</small></a>
                        &nbsp;
                        <a href="#" onClick={() => {setCurrentCustomer(null); openCustomerForm(); }}><small>Replace</small></a>
                      </div>}
                </Form.Label>

                {currentCustomer && currentCustomer.Type == 'contact' ?
                  <>
                      <div style={{ color: '#666', marginBottom: 12 }}>
                          <small>
                              {currentCustomer.Email && <div>{currentCustomer.Email}</div>}
                              {currentCustomer.Mobile && <div>{currentCustomer.Mobile}</div>}
                          </small>
                      </div>
                  </>
                  :
                  <ZohoCustomerSearch
                    emptyLabel={<div><strong>No matching contacts found.</strong></div>}
                    onSelect={setCurrentCustomer}
                    highlightOnlyResults={true}
                    enableSearch={true}
                    includeLeads={false}
                    allowNew={false}
                    />
                }
              {hasContact &&
                <AddressEditor
                  title="Billing Address"
                  prefix="Billing"
                  emptyLabel="No billing address set."
                  address={billingAddress}
                  editing={editingBillingAddress}
                  onEdit={() => setEditingBillingAddress(true)}
                  onClose={() => setEditingBillingAddress(false)}
                  onCopyFromCustomer={() => { setBillingEdited(true); setOrderData({ ...orderData, billingAddress: getBillingAddressFromCustomer() }); }}
                  onCopyFromOther={() => { setBillingEdited(true); setOrderData({ ...orderData, billingAddress: { ...shippingAddress } }); }}
                  copyFromOtherLabel="Copy from Installation"
                  updateAddress={updateBilling}
                />
              }
              {
                showDealerFields() && <>
                  <Form.Group>
                    <Form.Label>Dealer PO:</Form.Label>
                    <Form.Control type="text" onChange={(e) => setOrderData({ ...orderData, dealerPO: e.target.value })} />
                  </Form.Group>
                </>
              }
              </Col>
              <Col sm={6}>
                {hasContact &&
                  <AddressEditor
                    title="Installation Address"
                    prefix="Installation"
                    emptyLabel="No delivery address set."
                    address={shippingAddress}
                    editing={editingShippingAddress}
                    onEdit={() => setEditingShippingAddress(true)}
                    onClose={() => setEditingShippingAddress(false)}
                    onCopyFromCustomer={() => { setShippingEdited(true); setOrderData({ ...orderData, shippingAddress: getAddressFromCustomer() }); }}
                    onCopyFromOther={() => { setShippingEdited(true); setOrderData({ ...orderData, shippingAddress: { ...billingAddress } }); }}
                    copyFromOtherLabel="Copy from Billing"
                    updateAddress={updateShipping}
                  />
                }
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Col sm={6}>
                <Form.Label>Estimated Delivery Date:</Form.Label>
                <Form.Control
                  type="date"
                  onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    const currentDate = new Date();

                    if (selectedDate < currentDate) {
                      // If selected date is in the past, use the min date
                      e.target.value = '';
                    }

                    // Update state with the potentially corrected value
                    setOrderData({ ...orderData, dateRequired: e.target.value })
                  }}
                  min={date}
                  value={orderData.dateRequired}
                  />
                </Col>

                <Col sm={6}>
                  <Form.Label>Territory:</Form.Label>
                  <Form.Control required as="select" onChange={(e) => setOrderData({ ...orderData, salesType: e.target.value })} value={orderData.salesType}>
                    {renderSalesTypes.length > 1 && <option value=""></option>}
                    {renderSalesTypes.map(_ => <option value={_['code']} key={'salesType-' + _['code']}>{_['code']} - {_['description']}</option>)}
                  </Form.Control>
                </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Col sm={6}>
                <Form.Label>Warehouse:</Form.Label>
                <Form.Control required as="select" onChange={(e) => setOrderData({ ...orderData, warehouseCode: e.target.value })} value={isSerialised ? products[0]?.warehouseCode : orderData.warehouseCode} disabled={isSerialised}>
                  <option value=""></option>
                  {Object.entries(warehouses).map(([code, name]) => <option value={code} key={'warehouse-' + code}>{code} - {name}</option>)}
                </Form.Control>
              </Col>

            </Form.Group>

            <OrderTable
              products={products}
              setOrder={setProducts}
              selectedUpsells={selectedUpsells}
              setSelectedUpsells={setSelectedUpsells}
              deposit={orderData.deposit}
              setDeposit={deposit => setOrderData({ ...orderData, deposit })}
              isSkinny={orderData.isSkinny}
              setIsSkinny={isSkinny => setOrderData({ ...orderData, isSkinny })}
              note={orderData.note}
              setNote={note => setOrderData({ ...orderData, note })}
              invalidReasons={orderInvalidReasons}
              setInvalidReasons={setOrderInvalidReasons}
              />

          </Form>
        </Modal.Body>
        <Modal.Footer>
          { createOpportunities() && (orderData.opportunityName || currentCustomer) &&
                <Form.Group as={Row}>
                    <Form.Label column xs='auto'>Opportunity:</Form.Label>
                    <Col xs='auto'>
                      <Form.Control style={{minWidth: 350}} type="text" onChange={(e) => {
                        setOrderData({
                          ...orderData,
                          opportunityName: e.target.value
                        });
                      }} value={orderData.opportunityName || `${customerAddress?.firstName}_${customerAddress?.lastName}-${formatDate(new Date())}`} />
                    </Col>
                </Form.Group>
            }

          <OverlayTrigger
            overlay={
              <Tooltip id="tooltip-button" style={isValid ? { display: 'none' } : {}}>
                {inValidReasons.map((reason, index) => (
                  <div key={index}>{reason}</div>
                ))}
              </Tooltip>
            }
          >

            <Button variant="success" onClick={addToOrderCb} disabled={!isValid || saving}>
              {saving ? 'Reserving stock...' : 'Save'}
            </Button>

          </OverlayTrigger>
        </Modal.Footer>
      </Modal>
    </>
  );
};
